import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Eye,
  EyeOff,
  Sun,
  Moon,
  User,
  Shield,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import Label from '../components/ui/label';
import Checkbox from '../components/ui/checkbox';
import logo from '../images/logo_12.png';
import heroSlideOne from '../images/vectorelements-1yo18QrrISs-unsplash.jpg';
import heroSlideTwo from '../images/alghozy-hbcjF8smXD4-unsplash.jpg';
import heroSlideThree from '../images/art-attack-Qqn9VFkdV6E-unsplash.jpg';
import heroSlideFour from '../images/luky-triohandoko-eecTcKqXpz8-unsplash.jpg';
import { toast } from 'sonner';
import { login as apiLogin, requestOtp, verifyOtp, getLoginTenants } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { cn } from '../lib/utils';

const Login = () => {
  const [loginType, setLoginType] = useState('customer');
  const [authMethod, setAuthMethod] = useState('otp');
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  
  const { login } = useAuth();
  const { branding, resolveBranding, logoSrc, brandDisplayName, portalName } = useBranding();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const otpInputsRef = useRef([]);
  const brandingResolveGen = useRef(0);

  // --- BRANDING & TENANT LOGIC ---
  const selectedTenant = useMemo(() => {
    const id = String(selectedTenantId || '');
    if (!id) return null;
    const fromList = tenants.find((t) => String(t.id) === id);
    const fromOpts = (branding.tenant_options || []).find((t) => String(t.id) === id);
    const brandingMatchesTenant = branding.tenant_id != null && String(branding.tenant_id) === id;

    const mergeLogo = (rowLogo) => {
      const trimmed = rowLogo != null ? String(rowLogo).trim() : '';
      if (trimmed) return rowLogo;
      if (brandingMatchesTenant && branding.logo_url) return branding.logo_url;
      return rowLogo || '';
    };

    if (fromList) return { ...fromList, logo_url: mergeLogo(fromList.logo_url) };
    if (fromOpts) {
      return {
        id: fromOpts.id,
        name: fromOpts.name,
        portal_name: fromOpts.portal_name,
        logo_url: mergeLogo(fromOpts.logo_url),
        primary_color: fromOpts.primary_color,
        accent_color: fromOpts.accent_color,
        support_email: fromOpts.support_email,
      };
    }
    return null;
  }, [tenants, selectedTenantId, branding.tenant_options, branding.tenant_id, branding.logo_url]);

  const effectiveBrandName = selectedTenant?.name || brandDisplayName || 'Cantik Tech';
  const effectivePortalName = selectedTenant?.portal_name || portalName || 'Support Redefined';
  const effectiveLogo = useMemo(() => {
    const raw = (selectedTenant?.logo_url && String(selectedTenant.logo_url).trim()) || logoSrc || '';
    return resolveMediaUrl(raw);
  }, [selectedTenant?.logo_url, logoSrc]);
  
  const effectiveSupportEmail = selectedTenant?.support_email || branding.support_email;
  const heroSlides = useMemo(() => [heroSlideOne, heroSlideTwo, heroSlideThree, heroSlideFour], []);
  
  const currentLogo = imgError ? logo : (effectiveLogo || logo);

  useEffect(() => {
    setImgError(false);
  }, [effectiveLogo]);

  useEffect(() => {
    const email = formData.email.trim().toLowerCase();
    if (!email.includes('@')) {
      if (tenants.length !== 1) setSelectedTenantId('');
      return;
    }
    const looksLikeFullDomain = email.length > 5 && email.includes('.');
    if (!looksLikeFullDomain) return;

    const gen = ++brandingResolveGen.current;
    const timer = setTimeout(async () => {
      const merged = await resolveBranding({ email });
      if (gen !== brandingResolveGen.current) return;
      if (merged?.tenant_id != null && merged.tenant_id !== '') {
        setSelectedTenantId(String(merged.tenant_id));
        return;
      }
      if (Array.isArray(merged?.tenant_options) && merged.tenant_options.length > 0) {
        setSelectedTenantId(String(merged.tenant_options[0].id));
        return;
      }
      if (tenants.length !== 1) setSelectedTenantId('');
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, resolveBranding, loginType, tenants.length]);

  useEffect(() => {
    if (!selectedTenant) return;
    const root = document.documentElement;
    const nextPrimary = selectedTenant.primary_color || '#00d2ff';
    const nextAccent = selectedTenant.accent_color || '#7a28cb';
    root.style.setProperty('--tenant-primary', nextPrimary);
    root.style.setProperty('--tenant-accent', nextAccent);
  }, [loginType, selectedTenant]);

  useEffect(() => {
    const portalKey = loginType === 'admin' ? 'admin' : 'customer';
    const savedEmail = localStorage.getItem(`rememberedEmail_${portalKey}`);
    const savedPassword = localStorage.getItem(`rememberedPassword_${portalKey}`);
    const rememberMe = localStorage.getItem(`rememberMe_${portalKey}`) === 'true';

    if (rememberMe && savedEmail && savedPassword && loginType === 'admin' && authMethod === 'password') {
      setFormData({ email: savedEmail, password: savedPassword, otp: '', rememberMe: true });
    } else {
      setFormData({ email: savedEmail || '', password: '', otp: '', rememberMe: false });
    }
  }, [loginType, authMethod]);

  useEffect(() => {
    if (!['admin', 'customer'].includes(loginType)) return;
    let mounted = true;
    const loadTenants = async () => {
      try {
        const res = await getLoginTenants();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!mounted) return;
        setTenants(list);
      } catch (_err) {
        if (mounted) setTenants([]);
      }
    };
    loadTenants();
    return () => { mounted = false; };
  }, [loginType]);

  useEffect(() => {
    if (tenants.length !== 1) return;
    setSelectedTenantId(String(tenants[0].id));
  }, [tenants]);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // --- HANDLERS ---
  const handleTypeChange = (type) => {
    setLoginType(type);
    setStep(1);
    setFormData((prev) => ({ ...prev, email: '', password: '', otp: '' }));
    setAuthMethod(type === 'customer' ? 'otp' : 'password');
  };

  const handleAuthMethodChange = (method) => {
    setAuthMethod(method);
    setStep(1);
    setFormData((prev) => ({ ...prev, password: '', otp: '' }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleOtpBoxChange = (index, value) => {
    const digit = value.replaceAll(/\D/g, '').slice(-1);
    const current = formData.otp.padEnd(6, ' ').split('');
    current[index] = digit || ' ';
    const nextOtp = current.join('').trimEnd();
    setFormData((prev) => ({ ...prev, otp: nextOtp }));
    if (digit && index < 5 && otpInputsRef.current[index + 1]) {
      otpInputsRef.current[index + 1].focus();
    }
  };

  const handleOtpBoxKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const current = formData.otp.padEnd(6, ' ').split('');
      if (!current[index].trim() && index > 0 && otpInputsRef.current[index - 1]) {
        otpInputsRef.current[index - 1].focus();
      }
    }
  };

  const applyPastedOtp = (text) => {
    const digits = String(text || '').replaceAll(/\D/g, '').slice(0, 6);
    if (!digits) return;
    setFormData((prev) => ({ ...prev, otp: digits }));
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => otpInputsRef.current[focusIndex]?.focus(), 0);
  };

  const handleOtpPasteCapture = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!text.replaceAll(/\D/g, '')) return;
    e.preventDefault();
    e.stopPropagation();
    applyPastedOtp(text);
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!formData.email) return toast.error('Email is required');
    if (!selectedTenantId) return toast.error('We could not detect your organization from this email. Use your work email domain.');

    setIsLoading(true);
    try {
      const response = await requestOtp(formData.email, selectedTenantId);
      if (response.success) {
        setStep(2);
        toast.success('Code sent!', { description: `Verification code sent to ${formData.email}` });
      } else {
        toast.error(response?.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!formData.otp || formData.otp.length !== 6) return toast.error('Please enter the 6-digit code');
    if (!selectedTenantId) return toast.error('We could not detect your organization from this email.');

    setIsLoading(true);
    try {
      const response = await verifyOtp(formData.email, formData.otp, selectedTenantId);
      if (response.success) {
        completeLogin(response.data.user, response.data.token);
      } else {
        toast.error(response?.message || 'Invalid or expired code');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return toast.error('We could not detect your organization. Use your work email domain.');
    
    setIsLoading(true);
    try {
      const response = await apiLogin({ email: formData.email, password: formData.password, tenant_id: selectedTenantId });
      if (response.success) {
        const user = response.data.user;
        const role = (user.role || '').toString().toLowerCase().trim();
        const isAdminOrEmployee = role === 'admin' || role === 'employee' || role === 'super_admin' || role === 'global_support';

        if (loginType === 'admin' && !isAdminOrEmployee) return toast.error('Access Denied', { description: 'Please use the Customer login tab.' });
        if (loginType !== 'admin' && isAdminOrEmployee) return toast.error('Access Denied', { description: 'Please use the Admin login tab.' });

        const portalKey = loginType === 'admin' ? 'admin' : 'customer';
        if (formData.rememberMe) {
          localStorage.setItem(`rememberedEmail_${portalKey}`, formData.email);
          localStorage.setItem(`rememberedPassword_${portalKey}`, formData.password);
          localStorage.setItem(`rememberMe_${portalKey}`, 'true');
        } else {
          localStorage.removeItem(`rememberedEmail_${portalKey}`);
          localStorage.removeItem(`rememberedPassword_${portalKey}`);
          localStorage.removeItem(`rememberMe_${portalKey}`);
        }

        completeLogin(user, response.data.token);
      } else {
        const errorMessage = response?.message || 'Login failed';
        const invalidCreds =
          errorMessage.toLowerCase().includes('invalid email or password') ||
          errorMessage.toLowerCase().includes('invalid password');
        if (invalidCreds) {
          toast.error('Invalid credentials', {
            description: 'Please check your email/password and try again.',
          });
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      const invalidCreds =
        error.response?.status === 401 ||
        errorMessage.toLowerCase().includes('invalid email or password') ||
        errorMessage.toLowerCase().includes('invalid password');
      if (invalidCreds) {
        toast.error('Invalid credentials', {
          description: 'Please check your email/password and try again.',
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const completeLogin = (user, token) => {
    login(user, token);
    toast.success('Welcome back!', { description: `Signed in as ${user.name}` });
    const role = (user.role || '').toLowerCase();
    navigate(
      role === 'super_admin' ? '/super-admin/tenants' :
      role === 'global_support' ? '/admin/tickets' :
      role === 'admin' ? '/admin/dashboard' :
      role === 'employee' ? '/employee/dashboard' :
      '/customer/dashboard',
      { replace: true }
    );
  };

  return (
    <main
      className={cn(
        'min-h-screen w-full flex font-sans transition-colors duration-300',
        isDark ? 'bg-[#090b14] text-slate-200' : 'bg-slate-100 text-slate-800'
      )}
    >
      <button
        type="button"
        onClick={toggleTheme}
        className={cn(
          'fixed top-4 right-4 z-50 inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-all',
          isDark
            ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
        )}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        {isDark ? 'Light' : 'Dark'}
      </button>
      
      {/* 2-Column Split Layout */}
      <div className="w-full grid lg:grid-cols-2 min-h-screen">
        
        {/* LEFT COLUMN: Image & Overlay */}
        <section className={cn('hidden lg:flex flex-col justify-end relative overflow-hidden', isDark ? 'bg-[#090b14]' : 'bg-slate-200')}>
          {/* Full Height Background Slider */}
          <img
            src={heroSlides[heroSlideIndex]}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          />
          
          {/* Softer overlay to keep slideshow images bright */}
          <div className={cn("absolute inset-0 mix-blend-multiply", isDark ? "bg-[#090b14]/24" : "bg-transparent")} />
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-t',
              isDark
                ? 'from-[#090b14]/68 via-[#090b14]/35 to-transparent'
                : 'from-slate-100/70 via-slate-100/35 to-transparent'
            )}
          />

          {/* Branding Content - Raw text directly over the gradient, styled for contrast */}
          <div className="relative z-10 p-12 xl:p-20 w-full max-w-2xl mb-4">
            
            <div className="flex items-center gap-5 mb-6">
              
              {/* Logo Container - Perfect White Square */}
              <div className="w-16 h-16 shrink-0 bg-white rounded-2xl flex items-center justify-center p-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.15)]">
                <img
                  key={currentLogo} 
                  src={currentLogo}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={() => setImgError(true)}
                />
              </div>
              
              {/* Heading Container */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight break-words drop-shadow-lg bg-gradient-to-r from-[#00d2ff] to-[#7a28cb] bg-clip-text text-transparent">
                  {effectiveBrandName}
                </h1>
                <p className={cn('text-sm mt-1 tracking-widest uppercase font-bold truncate drop-shadow-md', isDark ? 'text-[#00d2ff]' : 'text-blue-600')}>
                  {effectivePortalName}
                </p>
              </div>
            </div>
            
            <p className="text-lg leading-relaxed mt-2 font-medium drop-shadow-md text-black">
              Welcome to {effectiveBrandName} Support. Submit tickets, track progress, and get help fast. Sign in to access your dashboard.
            </p>
            
          </div>
        </section>

        {/* RIGHT COLUMN: Glass Login Card */}
        <section className={cn('flex items-center justify-center p-6 sm:p-10 w-full relative', isDark ? 'bg-[#090b14]' : 'bg-slate-100')}>
          
          {/* ENHANCED Glow effects */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <div className={cn("absolute top-[5%] right-[5%] w-[450px] h-[450px] rounded-full blur-[100px] opacity-70", isDark ? "bg-[#7a28cb]/30" : "bg-[#7a28cb]/10")} />
            <div className={cn("absolute bottom-[5%] left-[5%] w-[450px] h-[450px] rounded-full blur-[100px] opacity-70", isDark ? "bg-[#00d2ff]/25" : "bg-[#00d2ff]/10")} />
          </div>

          {/* Form Card */}
          <div
            className={cn(
              'relative z-10 w-full max-w-[480px] min-h-[580px] rounded-[2rem] backdrop-blur-2xl flex flex-col overflow-hidden transition-all duration-300',
              isDark
                ? 'bg-[#111628]/40 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
                : 'bg-white/80 border border-slate-200 shadow-[0_8px_28px_rgba(15,23,42,0.08)]'
            )}
          >
            <div className="p-8 sm:p-12 pb-8 flex-1 flex flex-col">
              
              <div className="mb-8 text-center animate-in fade-in slide-in-from-top-4 duration-500">
                <h2 className={cn(
                  'text-3xl font-extrabold tracking-tight',
                  isDark 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#00d2ff] to-[#b379df]' 
                    : 'text-slate-900'
                )}>
                  ITSM Portal
                </h2>
                <p className={cn('text-sm mt-1.5 font-medium', isDark ? 'text-slate-400' : 'text-slate-600')}>Sign in to continue</p>
              </div>

              {/* Portal Switcher */}
              <div className={cn('flex p-1.5 rounded-full mb-10 relative shadow-inner', isDark ? 'bg-[#090b14]/50 border border-white/5' : 'bg-slate-100 border border-slate-200')}>
                <button
                  onClick={() => handleTypeChange('customer')}
                  className={cn(
                    'flex-1 py-3.5 rounded-full text-[15px] font-bold transition-all duration-500 z-10 flex items-center justify-center gap-2',
                    loginType === 'customer' 
                      ? 'bg-gradient-to-r from-[#00d2ff] via-[#4682ea] to-[#7a28cb] bg-[length:200%_auto] hover:bg-[position:right_center] text-white shadow-[0_0_20px_rgba(0,210,255,0.3)]' 
                      : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  )}
                >
                  <User className="w-4 h-4" />
                  User
                </button>
                <button
                  onClick={() => handleTypeChange('admin')}
                  className={cn(
                    'flex-1 py-3.5 rounded-full text-[15px] font-bold transition-all duration-500 z-10 flex items-center justify-center gap-2',
                    loginType === 'admin' 
                      ? 'bg-gradient-to-r from-[#00d2ff] via-[#4682ea] to-[#7a28cb] bg-[length:200%_auto] hover:bg-[position:right_center] text-white shadow-[0_0_20px_rgba(122,40,203,0.3)]' 
                      : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  )}
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
              </div>

              {/* Form Container */}
              <div className="flex-1 flex flex-col justify-start min-h-[360px]">
                {authMethod === 'password' ? (
                  <form onSubmit={handlePasswordLogin} className="space-y-6 min-h-[360px]">
                    <div className="space-y-2">
                      <Label className={cn('text-sm font-medium ml-1', isDark ? 'text-slate-300' : 'text-slate-700')}>Email</Label>
                      <div className="relative group">
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={cn(
                            'h-14 text-base rounded-[14px] pl-4 pr-12 transition-all duration-300 shadow-inner focus:ring-2 focus:ring-offset-0',
                            isDark
                              ? 'bg-white/[0.03] border-white/10 text-white focus:bg-white/[0.06] focus:border-[#00d2ff]/50 focus:ring-[#00d2ff]/30'
                              : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#00d2ff]/60 focus:ring-[#00d2ff]/20'
                          )}
                          required
                        />
                        <User className={cn('absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors', isDark ? 'text-slate-500 group-focus-within:text-[#00d2ff]' : 'text-slate-400 group-focus-within:text-[#00d2ff]')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className={cn('text-sm font-medium ml-1', isDark ? 'text-slate-300' : 'text-slate-700')}>Password</Label>
                      <div className="relative group">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className={cn(
                            'h-14 text-base rounded-[14px] pl-4 pr-12 transition-all duration-300 shadow-inner focus:ring-2 focus:ring-offset-0',
                            isDark
                              ? 'bg-white/[0.03] border-white/10 text-white focus:bg-white/[0.06] focus:border-[#00d2ff]/50 focus:ring-[#00d2ff]/30'
                              : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#00d2ff]/60 focus:ring-[#00d2ff]/20'
                          )}
                          required
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className={cn('absolute right-4 top-1/2 -translate-y-1/2 transition-colors', isDark ? 'text-slate-500 hover:text-white group-focus-within:text-[#00d2ff]' : 'text-slate-500 hover:text-slate-800 group-focus-within:text-[#00d2ff]')}>
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <label className={cn('flex items-center gap-2 cursor-pointer text-sm transition-colors', isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800')}>
                        <Checkbox 
                          checked={formData.rememberMe} 
                          onCheckedChange={(c) => setFormData((p) => ({ ...p, rememberMe: !!c }))} 
                          className="w-4 h-4 border-white/20 bg-white/5 rounded data-[state=checked]:bg-[#7a28cb] data-[state=checked]:border-[#7a28cb]" 
                        />
                        Remember me
                      </label>
                      <button type="button" onClick={() => handleAuthMethodChange('otp')} className={cn('text-sm transition-colors font-medium', isDark ? 'text-slate-400 hover:text-[#00d2ff]' : 'text-slate-600 hover:text-[#00d2ff]')}>
                        Forgot Password?
                      </button>
                    </div>

                    <Button disabled={isLoading} className="w-full h-14 mt-4 rounded-full bg-gradient-to-r from-[#00d2ff] via-[#4682ea] to-[#7a28cb] bg-[length:200%_auto] hover:bg-[position:right_center] text-white text-lg font-bold transition-all duration-500 shadow-[0_0_15px_rgba(122,40,203,0.3)] hover:shadow-[0_0_25px_rgba(0,210,255,0.4)] hover:scale-[1.02] active:scale-[0.98]">
                      {isLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'Log In'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={step === 1 ? handleRequestOtp : handleVerifyOtp} className="space-y-6 min-h-[360px]">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className={cn('text-sm font-medium ml-1', isDark ? 'text-slate-300' : 'text-slate-700')}>Email Address</Label>
                        {step === 2 && (
                          <button type="button" onClick={() => setStep(1)} className="text-xs text-[#00d2ff] hover:text-[#7a28cb] flex items-center gap-1 transition-colors">
                            <ArrowLeft className="w-3 h-3" /> Edit
                          </button>
                        )}
                      </div>
                      <div className="relative group">
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                          placeholder="pardeep.kumar@cantiktech.com"
                          className={cn(
                            'h-14 text-base rounded-[14px] pl-4 pr-12 transition-all duration-300 shadow-inner focus:ring-2 focus:ring-offset-0',
                            isDark
                              ? 'bg-white/[0.03] border-white/10 text-white focus:bg-white/[0.06] focus:border-[#00d2ff]/50 focus:ring-[#00d2ff]/30'
                              : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#00d2ff]/60 focus:ring-[#00d2ff]/20'
                          )}
                          disabled={step === 2}
                          required
                        />
                        <User className={cn('absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors', isDark ? 'text-slate-500 group-focus-within:text-[#00d2ff]' : 'text-slate-400 group-focus-within:text-[#00d2ff]')} />
                      </div>
                      {step === 1 && (
                        <p className={cn('text-xs mt-2 ml-1', isDark ? 'text-slate-400' : 'text-slate-600')}>
                          We&apos;ll send a 6-digit verification code to this email.
                        </p>
                      )}
                    </div>

                    {step === 2 && (
                      <div className="space-y-3 pt-2">
                        <Label className={cn('text-sm font-medium ml-1', isDark ? 'text-slate-300' : 'text-slate-700')}>Verification Code</Label>
                        <div
                          className="flex items-center justify-between gap-3"
                          onPaste={handleOtpPasteCapture}
                        >
                          {Array.from({ length: 6 }).map((_, idx) => {
                            const chars = formData.otp.padEnd(6, ' ').split('');
                            return (
                              <input
                                key={idx}
                                ref={(el) => (otpInputsRef.current[idx] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={chars[idx].trim()}
                                onChange={(e) => handleOtpBoxChange(idx, e.target.value)}
                                onKeyDown={(e) => handleOtpBoxKeyDown(idx, e)}
                                className={cn(
                                  'w-12 h-14 sm:w-14 sm:h-14 rounded-[12px] text-center text-2xl font-bold focus:outline-none transition-all duration-300 shadow-inner focus:ring-2',
                                  isDark
                                    ? 'bg-white/[0.03] border border-white/10 text-white focus:border-[#00d2ff]/50 focus:bg-white/[0.06] focus:ring-[#00d2ff]/30'
                                    : 'bg-white border border-slate-300 text-slate-900 focus:border-[#00d2ff]/60 focus:ring-[#00d2ff]/20'
                                )}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <Button type="submit" disabled={isLoading} className="w-full h-14 mt-6 rounded-full bg-gradient-to-r from-[#00d2ff] via-[#4682ea] to-[#7a28cb] bg-[length:200%_auto] hover:bg-[position:right_center] text-white text-lg font-bold transition-all duration-500 shadow-[0_0_15px_rgba(122,40,203,0.3)] hover:shadow-[0_0_25px_rgba(0,210,255,0.4)] hover:scale-[1.02] active:scale-[0.98]">
                      {isLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : step === 1 ? 'Get Secure Code' : 'Log In'}
                    </Button>

                  </form>
                )}
              </div>
            </div>

            {/* Footer Area */}
            <div className={cn('mt-auto py-5 text-center shrink-0 border-t', isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200')}>
              <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-700')}>
                Having trouble logging in?
              </p>
              <a
                href={`mailto:${effectiveSupportEmail || 'support@cantiktech.com'}`}
                className={cn('text-sm transition-colors font-medium', isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900')}
              >
                Contact Cantik support at{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d2ff] to-[#7a28cb] font-bold">
                  {effectiveSupportEmail || 'support@cantiktech.com'}
                </span>
              </a>
            </div>

          </div>
        </section>
      </div>
    </main>
  );
};

export default Login;